/**
 * What a signed-in credential is, said the way a person recognises it: "FEATit for Mac", "Safari
 * on iPhone", "FEATit CLI" (I-1034, `docs/concept-machine-client-credentials.md` §7).
 *
 * ADR-051 §5: a label is what a person reads when deciding whether to revoke. The audience alone
 * answers "Browser session" for the Mac app, for Safari and for Chrome alike, so the name is derived
 * here, when it is read, from what the sign-in said about itself. Derived rather than stamped at
 * mint, so a session minted before this existed is named too, and a refresh that brings a newer
 * user agent renames it.
 */

import {
  DEFAULT_CREDENTIAL_LABELS,
  type CredentialAudienceType,
  type CredentialKindType,
} from './credentials.js';
import { SHELL_USER_AGENT_PRODUCT } from './shell-paths.js';

/**
 * The product token the Mac app puts in its webview's user agent (`applicationNameForUserAgent`,
 * `desktop_process_name.rs`). A positive mark: a WebKit user agent without `Safari/` is not
 * evidence of the app, because any embedded webview looks like that.
 */
export const DESKTOP_USER_AGENT_PRODUCT = 'FEATitDesktop';

/**
 * The headers a client sends when it signs in, to name the install it runs in (I-1034, §7.2.3).
 *
 * `id` is an install id: random per browser profile or app install, never a hardware identifier.
 * Two sign-ins carrying the same one are the same install, and the second replaces the first.
 * `name` is the machine's own name ("Jay's MacBook Pro"), and `model` a hardware model identifier
 * ("iPhone18,1") that {@link deviceModelName} reads.
 */
export const SIGN_IN_DEVICE_HEADERS = {
  id: 'x-featit-device-id',
  name: 'x-featit-device-name',
  model: 'x-featit-device-model',
  /** The Mac app's primary interface hardware address (I-1034 AC-6), `a4:83:e7:12:34:56`. */
  mac: 'x-featit-device-mac',
  /** What that interface is called on the Mac: "Wi-Fi", "Ethernet". */
  macInterface: 'x-featit-device-mac-interface',
  /**
   * F-0555 (D-0286 b): whether the person consented to the address being read (§ 25(1) TDDDG),
   * `granted` or `withheld`. Sent by the Mac app only; a browser or iOS has no address to consent to.
   */
  macConsent: 'x-featit-device-mac-consent',
} as const;

/** The person's answer about the Mac's network interface address (F-0555). */
export type MacAddressConsentType = 'granted' | 'withheld';

/** A consent header's value as stored, or `null` for anything that is not one of the two answers. */
export function normalizeMacConsent(value: unknown): MacAddressConsentType | null {
  if (typeof value !== 'string') return null;
  const normalized = value.trim().toLowerCase();
  return normalized === 'granted' || normalized === 'withheld' ? normalized : null;
}

const MAC_PATTERN = /^[0-9a-f]{2}(?::[0-9a-f]{2}){5}$/;

/** A hardware address as stored — lower-case, colon-separated — or `null` for anything else. */
export function normalizeMacAddress(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const normalized = value.trim().toLowerCase().replace(/-/g, ':');
  // iOS answers this constant to every app since iOS 7: not an address, and never stored.
  if (!MAC_PATTERN.test(normalized) || normalized === '02:00:00:00:00:00') return null;
  return normalized;
}

/**
 * Whether an address is locally administered — the bit macOS and iOS set on a private (rotating)
 * Wi-Fi address. Such an address is what the interface reports, but it names this network's
 * connection rather than the hardware, so Devices says so.
 */
export function isPrivateMacAddress(mac: string): boolean {
  return (Number.parseInt(mac.slice(0, 2), 16) & 0x02) === 0x02;
}

/**
 * A device header's value on the wire. HTTP header values are Latin-1 at best, and the name macOS
 * gives a computer has a curly apostrophe in it ("Jay’s MacBook Pro"), so the clients percent-encode
 * the UTF-8 and the server decodes it. Measured: an unencoded `’` is refused by Node's HTTP client
 * before it is sent.
 */
export function encodeDeviceHeaderValue(value: string): string {
  return encodeURIComponent(value);
}

/** The text a device header carries; a value that is not valid percent-encoding is kept as sent. */
export function decodeDeviceHeaderValue(value: string | null | undefined): string | null {
  if (typeof value !== 'string') return null;
  if (!value.includes('%')) return value;
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

/** The longest device name or model kept, the same cap a person's own label has. */
export const DEVICE_TEXT_MAX_LENGTH = 80;

const DEVICE_ID_PATTERN = /^[A-Za-z0-9._-]{8,64}$/;

/** An install id as stored, or `null` for anything that is not one. Never coerced into shape. */
export function normalizeDeviceId(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return DEVICE_ID_PATTERN.test(trimmed) ? trimmed : null;
}

/** A device name or model as stored: printable, single-line, capped; `null` when nothing is left. */
export function normalizeDeviceText(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  // eslint-disable-next-line no-control-regex
  const printable = value.replace(/[\u0000-\u001f\u007f]+/g, ' ').replace(/\s+/g, ' ');
  const trimmed = printable.trim().slice(0, DEVICE_TEXT_MAX_LENGTH).trim();
  return trimmed || null;
}

/**
 * Apple's hardware identifiers for the models a person is likely to sign in from. Data rather than
 * code, so a new model is one line. An identifier missing from here reads as its family ("iPhone"),
 * which is true, only less specific — a wrong model name would not be.
 *
 * ⛔ **Every entry is sourced, none is guessed.** Each pair is the `modelIdentifier` Apple ships in
 * Xcode 26.6's simulator device profiles (`xcrun simctl list devicetypes -j`, then each type's
 * `Contents/Resources/profile.plist`), read 2026-10-01. Those profiles carry one identifier per
 * model, so a model's other identifiers (an iPad's cellular variant) read as the family until
 * added from an equally checkable source.
 */
const APPLE_MODEL_NAMES: Record<string, string> = {
  'iPhone14,7': 'iPhone 14',
  'iPhone14,8': 'iPhone 14 Plus',
  'iPhone15,2': 'iPhone 14 Pro',
  'iPhone15,3': 'iPhone 14 Pro Max',
  'iPhone15,4': 'iPhone 15',
  'iPhone15,5': 'iPhone 15 Plus',
  'iPhone16,1': 'iPhone 15 Pro',
  'iPhone16,2': 'iPhone 15 Pro Max',
  'iPhone17,1': 'iPhone 16 Pro',
  'iPhone17,2': 'iPhone 16 Pro Max',
  'iPhone17,3': 'iPhone 16',
  'iPhone17,4': 'iPhone 16 Plus',
  'iPhone17,5': 'iPhone 16e',
  'iPhone18,1': 'iPhone 17 Pro',
  'iPhone18,2': 'iPhone 17 Pro Max',
  'iPhone18,3': 'iPhone 17',
  'iPhone18,4': 'iPhone Air',
  'iPhone18,5': 'iPhone 17e',
  'iPad14,9': 'iPad Air 11-inch (M2)',
  'iPad14,11': 'iPad Air 13-inch (M2)',
  'iPad15,3': 'iPad Air 11-inch (M3)',
  'iPad15,5': 'iPad Air 13-inch (M3)',
  'iPad15,7': 'iPad (A16)',
  'iPad16,2': 'iPad mini (A17 Pro)',
  'iPad16,4': 'iPad Pro 11-inch (M4)',
  'iPad16,6': 'iPad Pro 13-inch (M4)',
  'iPad16,9': 'iPad Air 11-inch (M4)',
  'iPad16,11': 'iPad Air 13-inch (M4)',
  'iPad17,2': 'iPad Pro 11-inch (M5)',
  'iPad17,4': 'iPad Pro 13-inch (M5)',
};

/**
 * The device's own name, or `null` when it says nothing a model does not. Without Apple's
 * device-name entitlement iOS answers "iPhone" or "iPad" for every device, which is no name.
 */
export function meaningfulDeviceName(name: string | null | undefined): string | null {
  const normalized = normalizeDeviceText(name);
  if (!normalized) return null;
  return /^(iPhone|iPad|iPod touch|Mac)$/i.test(normalized) ? null : normalized;
}

/** "iPhone 17 Pro" for `iPhone18,1`, the family for an unlisted identifier, or `null`. */
export function deviceModelName(identifier: string | null | undefined): string | null {
  const model = identifier?.trim();
  if (!model) return null;
  if (APPLE_MODEL_NAMES[model]) return APPLE_MODEL_NAMES[model];
  if (/^iPhone/i.test(model)) return 'iPhone';
  if (/^iPad/i.test(model)) return 'iPad';
  // Already a name a person reads ("iPhone 17 Pro", "Pixel 9"): kept as sent.
  return /\s/.test(model) ? model : null;
}

/** The picture a sign-in gets. */
export const CREDENTIAL_CLIENT_ICONS = [
  'laptop',
  'phone',
  'tablet',
  'browser',
  'terminal',
] as const;
export type CredentialClientIconType = (typeof CREDENTIAL_CLIENT_ICONS)[number];

/** What a credential says about where it was signed in, as `/auth/credentials` returns it. */
export interface ICredentialClientSource {
  kind?: CredentialKindType;
  audience: CredentialAudienceType;
  label?: string | null;
  userAgent?: string | null;
  deviceName?: string | null;
  deviceModel?: string | null;
}

export interface ICredentialClient {
  /** "FEATit for Mac", "Safari on iPhone", "FEATit CLI". */
  name: string;
  icon: CredentialClientIconType;
  /** One of FEATit's own apps rather than a browser: the badge says "This app", not "This browser". */
  app: boolean;
  /** The machine's own name where the client sent one ("Jay's MacBook Pro"); never a generic one. */
  machine: string | null;
}

/**
 * The browser in a user agent. Order matters: Edge and Opera also say `Chrome/`, Chrome says
 * `Safari/`. Exported for F-0364: an in-app feedback report's `source.context.browser` is this same
 * read over the request's own `User-Agent`, not a second parser.
 */
export function browserOf(userAgent: string): string | null {
  if (/\bEdg(e|A|iOS)?\//.test(userAgent)) return 'Edge';
  if (/\b(OPR|Opera)\//.test(userAgent)) return 'Opera';
  if (/\b(Firefox|FxiOS)\//.test(userAgent)) return 'Firefox';
  if (/\b(Chrome|CriOS|Chromium)\//.test(userAgent)) return 'Chrome';
  if (/\bVersion\/[\d.]+.*\bSafari\//.test(userAgent)) return 'Safari';
  return null;
}

/**
 * The system in a user agent. iPad before Mac: an iPad in desktop mode still says iPad sometimes.
 * Exported for F-0364 (see {@link browserOf}).
 */
export function systemOf(userAgent: string): string | null {
  if (/\biPad\b/.test(userAgent)) return 'iPad';
  if (/\biPhone\b/.test(userAgent)) return 'iPhone';
  if (/\bAndroid\b/.test(userAgent)) return 'Android';
  if (/\bWindows\b/.test(userAgent)) return 'Windows';
  if (/\bCrOS\b/.test(userAgent)) return 'ChromeOS';
  if (/\b(Macintosh|Mac OS X)\b/.test(userAgent)) return 'Mac';
  if (/\bLinux\b/.test(userAgent)) return 'Linux';
  return null;
}

function desktopAppName(userAgent: string): string | null {
  if (new RegExp(`\\b${DESKTOP_USER_AGENT_PRODUCT}/`).test(userAgent)) {
    const system = systemOf(userAgent);
    return system === 'Mac' || system === 'Windows' || system === 'Linux'
      ? `FEATit for ${system}`
      : 'FEATit desktop app';
  }
  // The Rust cloud transport's own requests (`cloud_transport.rs`) name the app without a system.
  if (/^FeatIt Desktop\b/i.test(userAgent.trim())) return 'FEATit desktop app';
  return null;
}

/**
 * "iPhone" or "iPad" from the iOS shell's own token, `FEATitShell/<version> (iOS; phone|pad)`.
 * Exported for F-0364 (see {@link browserOf}).
 */
export function shellIdiomOf(userAgent: string): 'iPhone' | 'iPad' | null {
  const match = new RegExp(`\\b${SHELL_USER_AGENT_PRODUCT}/\\S*\\s*\\(iOS; (\\w+)\\)`).exec(
    userAgent,
  );
  if (!match) return null;
  return match[1] === 'pad' ? 'iPad' : 'iPhone';
}

/**
 * The shell's own app version, from the same token (`FEATitShell/<version> (iOS; phone|pad)`).
 * `null` outside the shell — a browser's version is the browser's, not FEATit's, and nothing here
 * claims one.
 */
export function shellVersionOf(userAgent: string): string | null {
  const match = new RegExp(`\\b${SHELL_USER_AGENT_PRODUCT}/(\\S+)\\s*\\(iOS`).exec(userAgent);
  return match ? match[1] : null;
}

/** Where a credential was signed in, from what its sign-in said. Never blank. */
export function describeCredentialClient(source: ICredentialClientSource): ICredentialClient {
  const userAgent = source.userAgent?.trim() ?? '';
  const machine = meaningfulDeviceName(source.deviceName);

  if (source.audience === 'cli')
    return { name: 'FEATit CLI', icon: 'terminal', app: true, machine };

  const desktop = desktopAppName(userAgent);
  if (desktop) return { name: desktop, icon: 'laptop', app: true, machine };

  const model = deviceModelName(source.deviceModel);
  const idiom = shellIdiomOf(userAgent);
  if (source.audience === 'mobile' || idiom) {
    const device = model ?? idiom ?? (systemOf(userAgent) === 'iPad' ? 'iPad' : null);
    const tablet = /iPad/.test(device ?? '') || /\biPad\b/.test(userAgent);
    return {
      name: device ? `FEATit on ${device}` : DEFAULT_CREDENTIAL_LABELS.mobile,
      icon: tablet ? 'tablet' : 'phone',
      app: true,
      machine,
    };
  }

  if (source.audience !== 'web') {
    return {
      name: DEFAULT_CREDENTIAL_LABELS[source.audience],
      icon: 'browser',
      app: false,
      machine,
    };
  }
  const browser = browserOf(userAgent);
  const system = systemOf(userAgent);
  const name =
    browser && system
      ? `${browser} on ${system}`
      : (browser ?? (system ? `Browser on ${system}` : DEFAULT_CREDENTIAL_LABELS.web));
  return { name, icon: 'browser', app: false, machine };
}

/**
 * Whether the stored label is one a person gave. Until I-1034 every sign-in was stamped with its
 * audience's default ("Browser session"), and that stamp is not a choice anybody made — reading it
 * as one would keep every existing session under the name this exists to replace.
 */
export function isChosenCredentialLabel(source: ICredentialClientSource): boolean {
  const label = source.label?.trim();
  if (!label) return false;
  return (
    (source.kind ?? 'session') !== 'session' || label !== DEFAULT_CREDENTIAL_LABELS[source.audience]
  );
}

/**
 * The name a credential list shows. A person's own label wins. A session without one is named for
 * where it was signed in. Every other kind keeps the label it was minted with, or its audience's.
 */
export function credentialDisplayName(source: ICredentialClientSource): string {
  if (isChosenCredentialLabel(source)) return (source.label as string).trim();
  if ((source.kind ?? 'session') === 'session') return describeCredentialClient(source).name;
  return DEFAULT_CREDENTIAL_LABELS[source.audience];
}
